/**
 * Mutation hook for updating an existing promotion (Pricing Rule).
 *
 * Calls the update_promotion endpoint which updates editable fields
 * on an existing Pricing Rule. Used by the "Edit promotion" flow
 * in RunASaleModal's edit mode.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { pricingRuleQueryKeys } from "./pricingRuleQueryKeys";

// ── Types ────────────────────────────────────────────────────

export interface UpdatePromotionParams {
  pricing_rule_name: string;
  title?: string;
  discount_percentage?: number;
  rate?: number;
  min_qty?: number;
  customer_group?: string;
  valid_from?: string;
  valid_upto?: string;
  apply_multiple_pricing_rules?: number;
}

export interface UpdatePromotionResponse {
  name: string;
  title: string;
  rate_or_discount: string;
  discount_percentage: number | null;
  rate: number | null;
  min_qty: number | null;
  valid_from: string | null;
  valid_upto: string | null;
  customer_group: string | null;
  applicable_for: string | null;
}

// ── Hook ─────────────────────────────────────────────────────

export function useUpdatePromotion(itemCode: string) {
  const queryClient = useQueryClient();

  return useMutation<UpdatePromotionResponse, Error, UpdatePromotionParams>({
    mutationFn: (params: UpdatePromotionParams) =>
      frappeMethod<UpdatePromotionResponse>(
        "zaviago_backend.api.pricing_promotion_actions.update_promotion",
        params
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: pricingRuleQueryKeys.all(),
      });
    },
  });
}
